// @ts-nocheck TODO: fix types for the entire file

import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { IconButton, Button } from 'components/Buttons';
import { Typography } from 'components/Typography';
import palette from 'theme/palette';
import pxToEm from 'theme/pxToEm';
import { SLIDE_DOWN_ANIMATION_MS } from '../../config';

const slideDownAnimationSeconds = SLIDE_DOWN_ANIMATION_MS / 1000;

const slideDown = keyframes`
  80% {
    opacity: 1;
    max-height: ${pxToEm(200)};
    margin-bottom: ${pxToEm(24)};
  }

  100% {
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
  }
`;

const fadeOut = keyframes`
  80% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
`;

export const Item = styled.div<{ hide: boolean }>`
  margin-bottom: ${pxToEm(24)};
  will-change: opacity, max-height, margin-bottom;
  ${({ hide }) =>
    hide &&
    css`
      animation: ${slideDown} ${slideDownAnimationSeconds}s ease-in-out forwards;
    `};
`;

export const CompactItem = styled(Item)`
  width: calc(33.33% - ${pxToEm(8)});
  margin-bottom: 0;
  position: relative;
  overflow: hidden;
  ${({ hide }) =>
    hide &&
    css`
      animation: ${fadeOut} ${slideDownAnimationSeconds}s ease-in-out forwards;
    `};
  display: flex;
  flex-direction: column;
`;

export const OrderCardWrapper = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${pxToEm(12)};
  padding-top: ${({ compact }) => (compact ? pxToEm(16) : 0)};
  flex: 1;
`;

export const OrderCardInfo = styled.div<{ compact?: boolean }>`
  width: ${({ compact }) => (compact ? 'auto' : pxToEm(210))};
  flex: ${({ compact }) => (compact ? '1 0 auto' : 'none')};
`;

const getCompactOrderCardBrandNameStyles = (compact?: boolean) => {
  if (compact) {
    return css`
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      max-width: ${pxToEm(260)};
    `;
  } else {
    return css`
      overflow: inherit;
    `;
  }
};

export const OrderCardBrandName = styled(Typography)<{ compact?: boolean }>`
  margin-bottom: ${pxToEm(4)};
  ${({ compact }) => getCompactOrderCardBrandNameStyles(compact)};
`;

export const OrderCardDescription = styled(Typography)`
  margin-bottom: ${pxToEm(2)};
  word-wrap: break-word;

  &:last-of-type {
    margin-bottom: 0;
  }

  span {
    color: ${palette.grey['A400']};
  }
`;

export const OrderCardActions = styled.div`
  display: flex;
  gap: ${pxToEm(12)};
  align-items: center;
  justify-content: center;
  flex: 1;
  padding-top: ${pxToEm(4)};
`;

export const ActionIcon = styled(IconButton)`
  background-color: ${palette.common.white};
  margin-bottom: ${pxToEm(4)};
`;

export const ActionText = styled(Typography)`
  padding: ${pxToEm(4)} ${pxToEm(8)};
`;

export const OrderCardAction = styled.div`
  flex: none;
  width: ${pxToEm(156)};
  text-align: center;
  cursor: pointer;

  svg {
    color: ${palette.grey[800]};
  }

  &:hover ${ActionIcon} {
    background-color: ${palette.grey[800]};

    svg {
      color: ${palette.common.white};
    }
  }
`;

export const OrderCardButton = styled(Button)`
  flex: 1;
  height: ${pxToEm(48)};
`;

export const CloseButton = styled(IconButton)`
  display: none;
  width: 36px;
  height: 36px;
  border-radius: 50px;
  font-size: 0.65em;
  background: ${palette.secondary.main} !important;
  position: absolute;
  top: ${pxToEm(12)};
  right: ${pxToEm(12)};

  &:hover {
    background: ${palette.grey['300']} !important;
  }

  span {
    pointer-events: none;
  }
`;

export const OrderCard = styled.div`
  padding: ${pxToEm(16)};
  border-radius: ${pxToEm(20)};
  background-color: ${palette.grey[200]};
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;

  &:hover ${CloseButton} {
    display: flex;
  }
`;

export const OrderCardUserContactedRep = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex: 1;
  padding-top: ${pxToEm(4)};
  padding-right: ${pxToEm(112)};

  ${ActionIcon} {
    background-color: ${palette.grey[800]};

    svg {
      color: ${palette.common.white};
    }
`;

export const OrderCardUserContactedRepText = styled(Typography)`
  padding: ${pxToEm(4)} ${pxToEm(8)};
  text-transform: capitalize;
`;

export const InboxLink = styled.a`
  text-decoration: underline;
`;

const getActionStatusStyles = (variant: 'sent' | 'notYet') => {
  switch (variant) {
    case 'sent':
      return css`
        color: ${palette.common.white};
        background-color: ${palette.primary.main};
      `;
    case 'notYet':
      return css`
        color: ${palette.primary.main};
        background-color: ${palette.common.white};
      `;
  }
};

export const ActionStatus = styled.div<{ variant: 'sent' | 'notYet' }>`
  padding: ${pxToEm(12)} ${pxToEm(16)};
  border-radius: ${pxToEm(24)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${pxToEm(10)};
  width: 100%;
  height: ${pxToEm(48)};
  box-sizing: border-box;
  font-size: ${pxToEm(16)};
  ${({ variant }) => getActionStatusStyles(variant)};
  margin: ${pxToEm(4)} 0 0;
`;
